import { authFetch } from "../../../api";
import { useState } from "react";
import { useWishlist } from "../../context/WishlistContext";
import AnimatedSnackbar from "../../../STMPages/components/snackbars/AnimatedSnackbar";
import EligibilityModal from "../../components/Modal/CourseApplyModal/SAPEligibilityModal";
import { useProfile } from "../../context/ProfileContext";
import SAPProfileIncompleteModal from "../../components/Modal/CourseApplyModal/SAPProfileIncompleteModal";
import PremiumModal from "../../components/Modal/PremiumModal";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";

const SAP_CourseCard = ({ course }) => {
  const { wishlist, fetchWishlist } = useWishlist();
  const userId = localStorage.getItem("userId");
  const wishlisted = wishlist.includes(String(course.id));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [modal, setModal] = useState({ type: null, open: false });
  const { loading, profileStatus, checkUserProfile } = useProfile();
  const [enrolled, setEnrolled] = useState(false);

  const navigate = useNavigate();

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleAddWishlist = async () => {
    try {
      const response = await authFetch("course-wishlist/add-shortlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          course_id: [course.id],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.addedCourses?.length > 0) {
          showSnackbar(data.message, "success");
          fetchWishlist();
        } else if (data.alreadyPresent?.length > 0) {
          showSnackbar("This course is already in your wishlist.", "info");
        } else {
          showSnackbar(data.message || "No changes made", "info");
        }
      } else {
        showSnackbar(data.message || "Something went wrong", "error");
      }
    } catch (err) {
      console.error("Failed to add to wishlist:", err);
      showSnackbar("Failed to add to wishlist. Please try again.", "error");
    }
  };

  const handleRemoveWishlist = async () => {
    try {
      const response = await authFetch("course-wishlist/add-shortlist", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          course_id: [course.id],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.data?.length > 0) {
          showSnackbar(data.message || "Removed from wishlist", "success");
          fetchWishlist();
        } else {
          showSnackbar("Course was not in your wishlist.", "info");
        }
      } else {
        showSnackbar(data.message || "Failed to remove from wishlist", "error");
      }
    } catch (err) {
      console.error("Failed to remove from wishlist:", err);
      showSnackbar(
        "Failed to remove from wishlist. Please try again.",
        "error",
      );
    }
  };

  const handleCheck = async () => {
    const isEnrolled = await checkEnrolmentAccess();

    if (!isEnrolled) {
      // showSnackbar(
      //   "You need to be enrolled to access this feature.",
      //   "warning"
      // );
      setModal({ type: "enrolled", open: true });
      return;
    }

    // STEP 2 — call profile API
    const profileRes = await checkUserProfile();

    if (profileRes?.success && profileRes?.statusCode === 200) {
      // All details filled → open eligibility modal
      setModal({ type: "eligibility", open: true });
    } else {
      // Profile incomplete → open different modal
      setModal({ type: "profileIncomplete", open: true });
    }
  };

  const checkEnrolmentAccess = async () => {
    const response = await authFetch(
      `sap-route/check_enrolment-feature-access`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
        }),
      },
    );

    const data = await response.json();
    const isEnrolled = data?.data?.is_enrolled;

    setEnrolled(isEnrolled);
    return isEnrolled;
  };

  const encryptId = (id) => {
    return btoa(id.toString()); // Base64 encode
  };

  return (
    <>
      <AnimatedSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />

      {modal.open && modal.type === "enrolled" && (
        <PremiumModal onClose={() => setModal({ open: false, type: null })} />
      )}

      {modal.open && modal.type === "eligibility" && (
        <EligibilityModal
          course={course}
          onClose={() => setModal({ open: false, type: null })}
        />
      )}

      {modal.open && modal.type === "profileIncomplete" && (
        <SAPProfileIncompleteModal
          onClose={() => setModal({ open: false, type: null })}
        />
      )}

      <div className="font-inter w-full shrink-0 rounded-xl border border-[#F9FAFB] bg-white shadow-[0px_2px_6px_rgba(0,0,0,0.2)]">
        <div className="p-4">
          {/* Header Box */}
          <div className="overflow-hidden rounded-xl border border-[#E3D8F5] shadow-[0px_1px_2px_rgba(0,0,0,0.05)]">
            {/* Top */}
            <div className="flex items-center justify-between bg-[#FBF5FF] px-4 py-3">
              <div className="text-md font-semibold leading-none text-[#1E1E1E]">
                {course.course_name}
              </div>

              <button
                onClick={() =>
                  wishlisted
                    ? handleRemoveWishlist(course.id)
                    : handleAddWishlist(course.id)
                }
                className="rounded-full border border-[#E3D8F5] bg-white p-1.5
             shadow-[0px_1px_2px_rgba(0,0,0,0.05)]
             hover:bg-[#F4ECFF]
             flex items-center justify-center"
              >
                <Heart
                  className={`h-5 w-5 ${
                    wishlisted
                      ? "fill-[#9C27B0] text-[#9C27B0]"
                      : "text-[#9C27B0]"
                  }`}
                />
              </button>
            </div>

            {/* Intake */}
            <div className="bg-gradient-to-r from-[#FFFFFF] to-[#F0DEFD] px-4 py-2">
              <p className="text-[14px] font-semibold text-[#572575] leading-none">
                Intake:{" "}
                {new Date(0, course.intake_month_from - 1).toLocaleString(
                  "en-US",
                  {
                    month: "short",
                  },
                )}{" "}
                {course.intake_year_from}
              </p>
            </div>
          </div>

          {/* University */}
          <div className="my-4 rounded-2xl bg-white p-4 shadow-[0px_2px_10px_rgba(0,0,0,0.1)]">
            <div className="flex items-center gap-2 text-md text-[#313439]">
              <img src="/university.svg" className="h-5 w-5" />
              {course.university_name}
            </div>

            <div className="mt-2 flex items-center gap-2 text-md text-[#313439] mr-2">
              <img src="/location.svg" className="h-5 w-auto" />
              {course.country_name}
            </div>
          </div>

          {/* Fees */}
          <div className="mb-4 rounded-2xl bg-white p-4 shadow-[0px_2px_10px_rgba(0,0,0,0.1)]">
            <div className="mb-3 flex items-center gap-2 text-md text-[#313439] font-bold">
              <img src="/money.png" className="h-5 w-5" />
              Fees
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[12px] font-bold text-[#3F424C]">
                  {course.currency_code} {course.application_fee || 0}
                </p>
                <p className="text-[12px] text-[#5F6570]">Application Fees</p>
              </div>

              <div>
                <p className="text-[12px] font-bold text-[#3F424C]">
                  {course.currency_code} {course.total_tution_fees || 0}
                </p>
                <p className="text-[12px] text-[#5F6570]">Tuition Fees</p>
              </div>
            </div>
          </div>

          {/* Test Eligibility */}
          <div className="mb-4 rounded-2xl bg-white p-4 shadow-[0px_2px_10px_rgba(0,0,0,0.1)]">
            <div className="mb-3 flex items-center gap-2 text-md text-[#313439] font-bold">
              <img src="/notes.png" className="h-5 w-auto" />
              Test eligibility
            </div>

            <div className="grid grid-cols-4 gap-2">
              {course.englishTests?.slice(0, 4).map((test, idx) => (
                <div key={idx}>
                  <p className="text-[12px] font-bold text-[#3F424C]">
                    {test.overall}
                  </p>
                  <p className="text-[12px] text-[#5F6570]">{test.test_type}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-4 flex gap-4">
            <button
              onClick={() =>
                navigate(`/sap/courses/course-details/${encryptId(course.id)}`)
              }
              className="font-inter h-[44px] w-full rounded-[14px] border border-[#7E838D] text-[15px] font-semibold text-[#3F424C] hover:border-[#9CA3AF]"
            >
              View More
            </button>

            <button
              onClick={handleCheck}
              className="font-inter h-[44px] w-full rounded-[14px] bg-[#5B1E77] text-[15px] font-semibold text-white shadow-[0px_4px_10px_rgba(91,30,119,0.25)]"
            >
              Enroll Now
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SAP_CourseCard;
